import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { type MouseEvent, useState } from "react";
import type { ClientWithContact, Lead, UserProfile } from "../../../../client";
import { cn } from "../../../../components/utils/cn";
import { Image } from "../../../../components";
import { getImageUrl } from "../../../../utils/image";
import { useToggleActiveState } from "../utils/use-toggle-active-state";
import { useMatchingState } from "../../../../services/organtization/semantic-match";
import { useGetCompanies } from "../../../../services/organtization/company/get-companies";
import { LeadAssignmentButton } from "./LeadAssignmentButton";
import { LeadEventsButton } from "./LeadEventsButton";
import { LeadMemberButton } from "./LeadMemberButton";
import { LeadButton } from "./LeadButton";
import { LeadMenuButton } from "./LeadMenuButton";
import { LeadPanelContent } from "./LeadPanelContent";
import { LeadTemperatureButton } from "./LeadTemperatureButton";
import { LeadOpenLinkButton } from "./LeadOpenLinkButton";
import { LeadEditLinkButton } from "./LeadEditLinkButton";
import { NumberOfUnreadEvents } from "./NumberOfUnreadEvents";

interface Props {
  organizationId: string;
  profiles?: UserProfile[];
  client?: ClientWithContact;
  lead?: Lead;
  onUpdateLead?: (payload: Partial<Lead>) => void;
  onDeleteLead?: (leadId: string) => void;
  onLeadCreated?: () => void;
  profileId?: string;
  childLeads?: Lead[];
  onAssignProfile?: (profileId: string) => void;
  hideAssignment?: boolean;
  hideProfileList?: boolean;
  isCreatingChildLead?: boolean;
}

export const LeadRow = ({
  organizationId,
  profiles,
  client,
  lead,
  onUpdateLead,
  onDeleteLead,
  onLeadCreated,
  profileId,
  childLeads = [],
  onAssignProfile,
  hideAssignment,
  hideProfileList,
  isCreatingChildLead,
}: Props) => {
  const { activeState, toggleActiveState } = useToggleActiveState({
    leadId: lead?.id ?? null,
  });

  const { data: companies } = useGetCompanies(organizationId);
  const linkedCompanyId = (lead as any)?.companyId as string | undefined;
  const linkedCompany = linkedCompanyId
    ? companies?.find((c) => c.id === linkedCompanyId)
    : undefined;
  const resolvedCompanyName = linkedCompany?.name || lead?.companyName;
  const resolvedLogoId = linkedCompany?.logoId ?? lead?.companyLogoId;

  const [numberOfMatchingMembers, setNumberOfMatchingMembers] = useState<
    number | null
  >(null);

  const resolvedProfiles = profiles ?? [];
  const leadNumberOfUnreadEvents = lead?.numberOfUnreadEvents ?? 0;
  const matchingState = useMatchingState();
  const leadId = lead?.id;
  const isJobActive = leadId ? !!matchingState.leadIds[leadId] : false;
  const jobPhase = leadId
    ? (matchingState.phase[leadId] ?? "matching")
    : "matching";
  const jobProgress = leadId ? matchingState.progress[leadId] : undefined;

  const matchingSteps = 1 + (jobProgress?.totalProfiles || 4);
  const matchingCompleted =
    (jobProgress?.embeddingDone ? 1 : 0) + (jobProgress?.profilesScored ?? 0);
  const matchingPct = jobProgress?.totalProfiles
    ? Math.round((matchingCompleted / matchingSteps) * 100)
    : null;
  const matchingLabel =
    jobPhase === "parsing" ? "Reading assignment…" : "Matching candidates…";

  return (
    <li
      data-lead-id={lead?.id ?? "new"}
      className={cn(
        "flex flex-col rounded-2xl border border-gray-3 transition-colors duration-300 overflow-hidden",
        activeState && "lg:bg-gray-1",
      )}
    >
      <div
        className={cn(
          "hidden w-full lg:flex flex-row gap-4 items-center justify-between overflow-hidden md:overflow-auto pr-2",
          !lead && "lg:grid grid-cols-3",
          onAssignProfile !== undefined &&
            `cursor-pointer transition-colors duration-200 hover:bg-gray-2 ${activeState ? "rounded-t-2xl" : "rounded-2xl"}`,
          onAssignProfile !== undefined && activeState && "bg-gray-1",
        )}
        onClick={
          onAssignProfile !== undefined
            ? () => toggleActiveState("assignment")
            : undefined
        }
      >
        <div className="flex flex-col">
          <div className="flex flex-row gap-4 items-center">
            <LeadAssignmentButton
              active={
                onAssignProfile === undefined && activeState === "assignment"
              }
              onClick={() => toggleActiveState("assignment")}
              title={
                lead?.title ||
                lead?.parsedAssignmentData?.title ||
                lead?.assignmentInfo?.title
              }
              logoId={resolvedLogoId}
              companyName={resolvedCompanyName}
              disabled={lead?.status === "archived"}
              className={
                onAssignProfile !== undefined
                  ? "lg:hover:bg-transparent active:lg:bg-transparent active:scale-100"
                  : undefined
              }
            />

            {!onAssignProfile && (
              <LeadMemberButton
                active={activeState === "member"}
                onClick={() => toggleActiveState("member")}
                profileId={lead?.profileId ?? undefined}
                organizationId={organizationId}
                profiles={resolvedProfiles}
                numberOfMatchingMembers={
                  !lead?.profileId && numberOfMatchingMembers != null
                    ? numberOfMatchingMembers
                    : !lead?.profileId && lead?.matchingMembers !== 0
                      ? lead?.matchingMembers
                      : null
                }
                disabled={lead?.status === "archived"}
              />
            )}
          </div>
        </div>

        {!lead && (
          <div className="flex flex-row gap-1 items-center justify-end self-stretch">
            <div className="flex flex-row gap-1 items-end self-stretch">
              <LeadMenuButton
                className="px-4"
                onArchive={() => {}}
                archived={false}
                onDelete={() => {
                  if (onDeleteLead) {
                    onDeleteLead("new");
                  }
                }}
                profiles={resolvedProfiles}
              />
            </div>
          </div>
        )}

        {lead && lead.status !== "archived" && (
          <div
            className="flex flex-row gap-1 items-center self-stretch"
            onClick={(e) => e.stopPropagation()}
          >
            {lead.profileLinkId && (
              <LeadOpenLinkButton
                profileLinkId={lead.profileLinkId}
                className="hidden lg:flex"
              />
            )}

            {lead.profileLinkId && lead.id && (
              <LeadEditLinkButton
                leadId={lead.id ?? ""}
                className="hidden lg:flex"
              />
            )}

            {onAssignProfile !== undefined && childLeads.length > 0 && (
              <div className="flex items-center gap-1.5 px-2">
                <div className="relative flex -space-x-2">
                  {childLeads.slice(0, 3).map((child) => {
                    const profile = profiles?.find(
                      (p) => p.profileId === child.profileId,
                    );
                    const photoId = profile?.profilePhotoId;
                    const name =
                      typeof profile?.profileData?.name === "string"
                        ? profile.profileData.name
                        : "?";
                    const handleAvatarClick = (e: MouseEvent) => {
                      e.stopPropagation();
                      toggleActiveState("assignment");
                      setTimeout(() => {
                        const childRow = document.querySelector(
                          `[data-lead-id="${child.id}"]`,
                        );
                        if (!childRow) return;
                        const navigationBar =
                          document.getElementById("navigation-bar");
                        const y =
                          childRow.getBoundingClientRect().top +
                          window.scrollY -
                          (navigationBar?.clientHeight ?? 0) -
                          16;
                        window.scrollTo({ top: y, behavior: "smooth" });
                      }, 300);
                    };
                    return photoId ? (
                      <button
                        key={child.id}
                        type="button"
                        onClick={handleAvatarClick}
                        className="rounded-lg focus:outline-none hover:scale-110 transition-transform duration-150"
                      >
                        <Image
                          src={getImageUrl(photoId)!}
                          className="w-10 h-10 rounded-lg object-cover border-2 border-white"
                          alt={name}
                        />
                      </button>
                    ) : (
                      <button
                        key={child.id}
                        type="button"
                        onClick={handleAvatarClick}
                        className="w-10 h-10 rounded-lg bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-semibold text-gray-500 hover:scale-110 transition-transform duration-150 focus:outline-none"
                      >
                        {name[0] ?? "?"}
                      </button>
                    );
                  })}
                  <NumberOfUnreadEvents
                    numberOfUnreadEvents={childLeads.reduce(
                      (sum, c) => sum + (c.numberOfUnreadEvents ?? 0),
                      0,
                    )}
                    className="top-0 left-0 right-auto"
                  />
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleActiveState("assignment");
                  }}
                  className="flex flex-col items-start text-gray-500 hover:text-black transition-colors duration-150"
                >
                  <span className="text-xs font-semibold text-gray-600">
                    {childLeads.length}{" "}
                    {childLeads.length === 1 ? "profile" : "profiles"}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">
                    {(() => {
                      const firstNames = childLeads
                        .map((c) => {
                          const profile = profiles?.find(
                            (p) => p.profileId === c.profileId,
                          );
                          const fullName =
                            typeof profile?.profileData?.name === "string"
                              ? profile.profileData.name
                              : null;
                          return fullName ? fullName.split(" ")[0] : null;
                        })
                        .filter(Boolean) as string[];
                      if (firstNames.length === 0) return null;
                      const shown = firstNames.slice(0, 2);
                      const rest = firstNames.length - shown.length;
                      return rest > 0
                        ? `${shown.join(", ")} +${rest}`
                        : shown.join(", ");
                    })()}
                  </span>
                </button>
              </div>
            )}

            {!onAssignProfile && (
              <LeadEventsButton
                active={activeState === "events"}
                onClick={() => {
                  toggleActiveState("events");
                  // Reset the number of unread events to 0
                  if (leadNumberOfUnreadEvents > 0)
                    onUpdateLead?.({ numberOfUnreadEvents: 0 });
                }}
                numberOfUnreadEvents={leadNumberOfUnreadEvents}
              />
            )}
            {!onAssignProfile && (
              <LeadTemperatureButton
                priority={lead?.priority ?? 1}
                onPriorityChange={(priority) => onUpdateLead?.({ priority })}
                className="hidden lg:flex"
              />
            )}

            {onAssignProfile === undefined && (
              <LeadMenuButton
                className="px-4"
                profileLinkId={lead?.profileLinkId}
                onArchive={() =>
                  onUpdateLead?.({
                    status: lead.status === "archived" ? "" : "archived",
                  })
                }
                archived={lead.status === "archived"}
                onDelete={() => {
                  if (lead.id && onDeleteLead) {
                    onDeleteLead(lead.id);
                  }
                }}
                leadId={lead.id}
                profiles={resolvedProfiles}
                profileId={lead?.profileId ?? undefined}
              />
            )}
            {onAssignProfile !== undefined && (
              <div
                className="flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                {isJobActive && !activeState && (
                  <>
                    <style>{`
                      @keyframes matchGradientShift {
                        0% { background-position: 0% 50%; }
                        100% { background-position: 100% 50%; }
                      }
                    `}</style>
                    <div
                      className="flex items-center gap-1.5 text-xs text-gray-500 px-3 py-1.5 rounded-xl"
                      style={{
                        background:
                          "linear-gradient(90deg, rgba(57,220,234,0.05) 10%, rgba(255,40,166,0.05) 88%)",
                        backgroundSize: "200% 100%",
                        animation:
                          "matchGradientShift 3s ease-in-out infinite alternate",
                      }}
                    >
                      <span>{matchingLabel}</span>
                      {jobPhase === "matching" && matchingPct !== null && (
                        <span className="text-gray-400">{matchingPct}%</span>
                      )}
                    </div>
                  </>
                )}
                <LeadButton
                  onClick={() => toggleActiveState("assignment")}
                  className="chevron-btn justify-center"
                >
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${activeState ? "rotate-180" : ""}`}
                  />
                </LeadButton>
              </div>
            )}
          </div>
        )}
        {lead && lead.status === "archived" && (
          <div className="flex flex-row gap-1 items-center self-stretch">
            <LeadMenuButton
              className="px-4"
              profileLinkId={lead?.profileLinkId}
              onArchive={() =>
                onUpdateLead?.({
                  status: lead.status === "archived" ? "" : "archived",
                })
              }
              archived={lead.status === "archived"}
              onDelete={() => {
                if (lead.id && onDeleteLead) {
                  onDeleteLead(lead.id);
                }
              }}
              profiles={resolvedProfiles}
              profileId={lead?.profileId ?? undefined}
            />
          </div>
        )}
      </div>

      <div className="flex flex-row justify-between lg:hidden">
        <LeadAssignmentButton
          active={activeState === "assignment"}
          onClick={() => toggleActiveState("assignment")}
          title={
            lead?.title ||
            lead?.parsedAssignmentData?.title ||
            lead?.assignmentInfo?.title
          }
          logoId={resolvedLogoId}
          companyName={resolvedCompanyName}
          disabled={lead?.status === "archived"}
        />
        {lead?.status === "archived" && (
          <LeadMenuButton
            className="px-4"
            onArchive={() =>
              onUpdateLead?.({
                status: lead?.status === "archived" ? "" : "archived",
              })
            }
            archived={lead?.status === "archived"}
            id="lead-drawer-menu-menu"
            onDelete={() => {
              if (lead.id && onDeleteLead) {
                onDeleteLead(lead.id);
              }
            }}
            profiles={resolvedProfiles}
            profileId={lead?.profileId ?? undefined}
          />
        )}
      </div>

      {(activeState || isCreatingChildLead) && (
        <LeadPanelContent
          state={activeState}
          organizationId={organizationId}
          className="hidden lg:block"
          divider
          leadId={lead?.id}
          onMatchingMembers={(numberOfMatchingMembers: number) => {
            setNumberOfMatchingMembers(numberOfMatchingMembers);
          }}
          onLeadCreated={() => {
            if (onLeadCreated) {
              onLeadCreated();
            }
          }}
          profileId={profileId}
          onAssignProfile={onAssignProfile}
          hideAssignment={hideAssignment}
          hideProfileList={hideProfileList}
          childLeads={onAssignProfile !== undefined ? childLeads : undefined}
          isCreatingChildLead={isCreatingChildLead}
          onArchive={
            onAssignProfile !== undefined
              ? () => {
                  onUpdateLead?.({ status: "archived" });
                  toggleActiveState(false);
                }
              : undefined
          }
        />
      )}
    </li>
  );
};

LeadRow.displayName = "LeadRow";
